import React from "react";
import { message } from "antd";
import axiosInstance from "../../../helpers/axios";
import { responseDataFormate } from "../../../helpers/bycrypt";
import { WarningSeverity } from "./quotationTypes";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { ProductComponentTypeEnum } from "../Products/productsTypes";
import { deleteFromS3 } from "../../../helpers/awsUpload";
import { alias } from "../../../helpers/utils";

interface CreateQuoatationCustomerDetailsParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
  onDuplicateEmailError: (data: any) => void;
}

export const createQuotationCustomerDetailsAPI =
  ({
    payload,
    onSuccess,
    onError,
    onDuplicateEmailError,
  }: CreateQuoatationCustomerDetailsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`/quotes`, payload);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      onError?.();

      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message;
      if (status === 428) {
        if (onDuplicateEmailError) {
          onDuplicateEmailError(error?.response?.data);
        }
      } else {
        // Handle all other errors (500, 400, etc.)
        message.error(errorMessage || "Something went wrong");
      }
    }
  };

interface GetQuotationDetailsParams {
  quote_id: string;
  is_approve_page?: boolean;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  onNotFound?: () => void;
}

export const getQuotationDetailsAPI =
  ({
    quote_id,
    is_approve_page,
    onSuccess,
    onError,
    onNotFound,
  }: GetQuotationDetailsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/quotes/${quote_id}`, {
        params: is_approve_page ? { is_approve_page } : undefined,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Quotation error:", error);
      onError?.();

      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message;
      if (status === 404) {
        if (onNotFound) {
          onNotFound();
        }
      } else {
        // Handle all other errors (500, 400, etc.)
        message.error(errorMessage || "Something went wrong");
      }
    }
  };

const getAppBasePath = () => {
  if (alias) {
    const cleanAlias = alias.replace(/^\/+|\/+$/g, "");
    return cleanAlias ? `/${cleanAlias}` : "";
  }
  const pathname = window.location.pathname;
  const match = pathname.match(/^(.*?)\/(quotations|estimates|quote-requests)/);
  if (match && match[1]) {
    return match[1];
  }
  return "";
};

interface UpdateQuotationParams {
  payload: any;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  onNoItemsError?: () => void;
  onDuplicateEmailError?: (data: any) => void;
  onWarnings?: (warnings: any[], severity?: WarningSeverity) => void;
  onLocked?: () => void;
  navigate?: (path: string, options?: any) => void;
}

export const updateQuotationAPI =
  ({
    payload,
    onSuccess,
    onError,
    onNoItemsError,
    onDuplicateEmailError,
    onWarnings,
    onLocked,
    navigate,
  }: UpdateQuotationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(`/quotes/update`, payload);

      let result = responseDataFormate(response.data);

      if (response.status === 207) {
        console.log("Warnings", result.data?.warnings);
        onWarnings?.(result.data?.warnings || [], result.severity);
        return;
      }

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Quotation error:", error);
      onError?.();

      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message;
      if (status === 417) {
        const quoteId =
          payload?.quote_id ||
          payload?._id ||
          payload?.quotationId ||
          new URLSearchParams(window.location.search).get("quotationId");

        try {
          sessionStorage.setItem(
            "status_lock_redirect_toast",
            JSON.stringify({
              entity: "quotation",
              status: "Order Created",
            }),
          );
        } catch (e) {}

        message.info({
          content: React.createElement(
            "span",
            null,
            "This quotation has already been converted to ",
            React.createElement("strong", null, "Order Created"),
            ". So, cannot be edited.",
          ),
          key: "status_lock_redirect_toast",
        });
        const targetPath = quoteId ? `/quotations/${quoteId}` : `/quotations`;
        if (navigate) {
          navigate(targetPath, { replace: true });
        } else if (onLocked) {
          onLocked();
        } else {
          const basePath = getAppBasePath();
          window.location.href = `${basePath}${targetPath}`;
        }
        return;
      } else if (status === 422) {
        if (onNoItemsError) {
          onNoItemsError();
        }
      } else if (status === 428) {
        if (onDuplicateEmailError) {
          onDuplicateEmailError(error?.response?.data);
        }
      } else {
        // Handle all other errors (500, 400, etc.)
        message.error(errorMessage || "Something went wrong");
      }
    }
  };

export interface PossibleDimesionsFilterParams {
  product_id: string;
  width?: number;
  height?: number;
  depth?: number;
  thickness?: number;
  length?: number;
  category_id?: string;
}

interface GetPossibleDimesionsParams {
  filters: PossibleDimesionsFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getPossibleDimesionsAPI =
  ({ filters, onSuccess, onError }: GetPossibleDimesionsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        "/products/possible-dimensions",
        {
          params: filters,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Dimensions Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetVarinatPriceParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const getVariantPriceAPI =
  ({ payload, onSuccess, onError }: GetVarinatPriceParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        "/products/variants-with-pricing",
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Variant price error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

export interface QuotattionSevicesParams {
  page?: number;
  limit?: number;
  search?: string;
}

interface GetQuotationServicesParams {
  quote_id: string;
  filters: QuotattionSevicesParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getQuotationServicesListAPI =
  ({
    quote_id,
    filters,
    onSuccess,
    onError,
    signal,
  }: GetQuotationServicesParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/quotes/${quote_id}/services`, {
        params: filters,
        signal: signal,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Dimensions Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

export const getFactoryAssembledFramesQtyAPI =
  (quote_id: string) =>
  async (dispatch: any): Promise<{ qty: number; frame_qty: number } | null> => {
    try {
      const response = await axiosInstance.get(
        `/quotes/${quote_id}/factory-assembled-frames-qty`,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        return {
          qty: Number(result.data?.qty) || 0,
          frame_qty: Number(result.data?.frame_qty) || 0,
        };
      } else {
        message.error(result?.message);
        return null;
      }
    } catch (error: any) {
      if (error?.code === "ERR_CANCELED" || error?.name === "AbortError")
        return null;
      console.error("Get Factory Assembled Frames Qty Error:", error);
      message.error(
        error?.response?.data?.message ||
          "Failed to fetch factory assembled frames quantity",
      );
      return null;
    }
  };

interface CreateNoteParams {
  quote_id: string;
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createQuotationNoteAPI =
  ({ quote_id, payload, onSuccess, onError }: CreateNoteParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/${quote_id}/notes`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Quotation Note error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface UpdateQuotationNoteParams {
  quote_id: string;
  note_id: string;
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateQuotationNoteAPI =
  ({
    quote_id,
    note_id,
    payload,
    onSuccess,
    onError,
  }: UpdateQuotationNoteParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/quotes/${quote_id}/notes/${note_id}`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Quotation Note error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface DeleteQuotationNoteParams {
  quote_id: string;
  note_id: string;
  type: string;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteQuotationNoteAPI =
  ({
    quote_id,
    note_id,
    type,
    onSuccess,
    onError,
  }: DeleteQuotationNoteParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `/quotes/${quote_id}/notes/${note_id}`,
        {
          params: {
            type,
          },
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message || "Product deleted successfully");
        onSuccess?.();
      } else {
        message.error(result?.message || "Failed to delete Product");
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Product error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to delete Product";
      message.error(errorMessage);
    }
  };

export interface QuotationFilterParams {
  page?: number;
  limit?: number;
  status?: string | string[];
  customer_id?: string[];
  search?: string;
  date_from?: string;
  date_to?: string;
  sortBy?: string | null;
  sortOrder?: string | null;
  created_by?: string[];
  is_archived?: string;
}

interface GetQuotationListParams {
  filters: QuotationFilterParams;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getQuotationsListAPI =
  ({ filters, onSuccess, onError, signal }: GetQuotationListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/quotes", {
        params: filters,
        signal,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Quotation List Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

export const fetchQuotationsQueryFn = async (
  filters: QuotationFilterParams,
  signal?: AbortSignal,
) => {
  const response = await axiosInstance.get("/quotes", {
    params: filters,
    signal,
  });

  const result = responseDataFormate(response.data);

  if (result?.success) {
    return result.data;
  } else {
    throw new Error(result?.message || "Failed to fetch Quotations");
  }
};

interface DeleteQuotationParams {
  id: string;
  onSuccess: () => void;
  onError: () => void;
}

export const deleteQuotationAPI =
  ({ id, onSuccess, onError }: DeleteQuotationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(`/quotes`, {
        data: { quote_id: id },
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        // Clean up S3 files returned from the backend
        const filesRemoved: string[] = result?.data?.filesRemoved || [];
        if (filesRemoved.length > 0) {
          for (const fileUrl of filesRemoved) {
            try {
              if (!fileUrl) continue;
              let key = fileUrl;
              if (
                fileUrl.startsWith("http://") ||
                fileUrl.startsWith("https://")
              ) {
                const urlObj = new URL(fileUrl);
                key = urlObj.pathname.substring(1);
              } else if (key.startsWith("/")) {
                key = key.substring(1);
              }
              if (key) {
                await deleteFromS3(key).catch(() => {});
                const fileName = key.split("/").pop();
                if (fileName) {
                  await deleteFromS3(`thumbnails/${fileName}`).catch(() => {});
                }
              }
            } catch (s3Err) {
              console.error("Failed to delete S3 object:", fileUrl, s3Err);
            }
          }
        }
        message.success(result?.message);
        onSuccess?.();
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Quotation error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface ToggleArchiveQuoteParams {
  quote_id: string;
  is_archived: boolean;
  onSuccess: () => void;
  onError: () => void;
}

export const toggleArchiveQuoteAPI =
  ({ quote_id, is_archived, onSuccess, onError }: ToggleArchiveQuoteParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(`/quotes/archive`, {
        quote_id,
        is_archived,
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.();
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Toggle Archive error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface ChangeQuotationStatusPayload {
  quote_id: string;
  status:
    | "saved"
    | "sent"
    | "design_pending"
    | "approved_override"
    | "design_approved"
    | "pending_admin_approval"
    | "admin_approved";
  approval_note?: string;
  force_update?: boolean;
}

interface UpdateQuotationStatusParams {
  payload: ChangeQuotationStatusPayload;
  onSuccess: (data: any) => void;
  onError: () => void;
  onNoItemsError?: () => void;
  onWarnings?: (warnings: any[], severity?: WarningSeverity) => void;
}

export const updateQuotatioStatusAPI =
  ({
    payload,
    onSuccess,
    onError,
    onNoItemsError,
    onWarnings,
  }: UpdateQuotationStatusParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(`/quotes/status`, payload);

      let result = responseDataFormate(response.data);

      if (response.status === 207) {
        console.log("Warnings", result.data?.warnings);
        onWarnings?.(result.data?.warnings || [], result.severity);
        return;
      }

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Quotation Status error:", error);
      onError?.();

      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message;
      if (status === 422) {
        if (onNoItemsError) {
          onNoItemsError();
        }
      } else {
        // Handle all other errors (500, 400, etc.)
        message.error(errorMessage || "Something went wrong");
      }
    }
  };

interface UpdateQuotationIdParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateQuotationIdAPI =
  ({ payload, onSuccess, onError }: UpdateQuotationIdParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/quotes/update-quote-id`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Quotation Id error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface NotifyClientParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
  onWarnings?: (warnings: any[], severity?: WarningSeverity) => void;
}

export const notifyClientAPI =
  ({ payload, onSuccess, onError, onWarnings }: NotifyClientParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/notify-client`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (response.status === 207) {
        onWarnings?.(result.data?.warnings || [], result.severity);
        return;
      }

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Notify client error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface PreviewQuoteEmailParams {
  payload: { quote_id: string };
  onSuccess?: (data: {
    to?: string;
    cc?: string;
    mail_subject?: string;
    text_template?: string;
    html_template?: string;
    pdf_url?: string;
    pdf_name?: string;
  }) => void;
  onError?: () => void;
}

export const previewQuoteEmailAPI =
  ({ payload, onSuccess, onError }: PreviewQuoteEmailParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/email-preview`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to generate email preview");
        onError?.();
      }
    } catch (error: any) {
      console.error("Preview quote email error:", error);
      onError?.();

      const errorMessage =
        error?.response?.data?.message || "Unable to generate email preview";
      message.error(errorMessage);
    }
  };

export const openSystemMailer = (emailData: {
  to?: string;
  cc?: string;
  mail_subject?: string;
  html_template?: string;
}) => {
  const to = emailData.to || "";
  const cc = emailData.cc || "";
  const subject = emailData.mail_subject || "";
  const body = emailData.html_template || "";

  let mailtoUrl = `mailto:${encodeURIComponent(to)}`;
  const params: string[] = [];
  if (cc) params.push(`cc=${encodeURIComponent(cc)}`);
  if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
  if (body) params.push(`body=${encodeURIComponent(body)}`);

  if (params.length > 0) {
    mailtoUrl += `?${params.join("&")}`;
  }

  window.location.href = mailtoUrl;
};

interface GenerateQuotationPdfParams {
  payload: { quote_id: string };
  onSuccess?: () => void;
  onError?: () => void;
}

export const generateQuotationPdfAPI =
  ({ payload, onSuccess, onError }: GenerateQuotationPdfParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/generate-pdf`,
        payload,
        {
          responseType: "blob",
        },
      );

      let fileName = "Quotation.pdf";

      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      if (Capacitor.isNativePlatform()) {
        const reader = new FileReader();

        const blobToBase64 = (blob: Blob) =>
          new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

        try {
          const result: any = await blobToBase64(blob);
          const base64Data = result?.split(",")[1];

          const uniqueFileName = `${Date.now()}_${fileName}`;

          const savedFile = await Filesystem.writeFile({
            path: uniqueFileName,
            data: base64Data,
            directory: Directory.Cache,
          });

          await Share.share({
            url: savedFile.uri,
          });

          onSuccess?.();
        } catch (err) {
          console.error("Native Share Error:", err);
          onError?.();
        }
      } else {
        // 3. Create a hidden link element to trigger the download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        // 4. Set the dynamic filename
        link.setAttribute("download", fileName);

        // 5. Trigger click and cleanup
        document.body.appendChild(link);
        link.click();

        // Cleanup
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        onSuccess?.();
        message.success("Quotation PDF downloaded successfully!");
      }
    } catch (error: any) {
      console.error("PDF Generation error:", error);
      onError?.();

      // Helper to read the error message from a Blob (since responseType is blob)
      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorObj = JSON.parse(reader.result as string);
            message.error(errorObj.message || "Failed to generate PDF");
          } catch (e) {
            message.error("Failed to generate PDF");
          }
        };
        reader.readAsText(error.response.data);
      } else {
        message.error("Failed to generate PDF");
      }
    }
  };

interface ApproveClientParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const approveQuoteAPI =
  ({ payload, onSuccess, onError }: ApproveClientParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`/quotes/approve`, payload);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Approve Quotation error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface GetQuotationFiltersParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getQuotationFiltersAPI =
  ({ onSuccess, onError }: GetQuotationFiltersParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/quotes/filter-data");

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Quotation Filters Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetTermsAndConditionsParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getTermsAndConditionsAPI =
  ({ onSuccess, onError }: GetTermsAndConditionsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/staticPage/static/single?url=terms-and-conditions`,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Terms and Conditions Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface PayMilestoneParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const payMilestoneAPI =
  ({ payload, onSuccess, onError }: PayMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/payment-milestones/mark-as-paid`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Pay Milestone error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface UnpayMilestoneParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const unpayMilestoneAPI =
  ({ payload, onSuccess, onError }: UnpayMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/payment-milestones/mark-as-unpaid`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Un-Pay Milestone error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

export const createMilestoneInvoiceAPI =
  ({ payload, onSuccess, onError }: PayMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/payment-milestones/create-invoice`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Milestone Invoice error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

export const payMilestoneInvoiceAPI =
  ({ payload, onSuccess, onError }: PayMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/customers/payment-milestones/pay-invoice`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Pay Milestone Invoice error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface ReopenQuotationParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const reopenQuotationAPI =
  ({ payload, onSuccess, onError }: ReopenQuotationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`/quotes/reopen`, payload);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Reopen Quotation error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface AddQuotationMilestoneParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const addQuotationMilestoneAPI =
  ({ payload, onSuccess, onError }: AddQuotationMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/add-update-payment-milestone`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Add Quotation Milestone error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface DeleteQuotationMilestoneParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const deleteQuotationMilestoneAPI =
  ({ payload, onSuccess, onError }: DeleteQuotationMilestoneParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(
        `/quotes/delete-payment-milestone`,
        {
          data: payload,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        dispatch({
          type: "DELETE_MILESTONE_FROM_ORDER",
          payload: result?.data?.payment_milestones,
        });
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Quotation Milestone error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface GetProductTypesParams {
  category?: string;
  quote_id?: string;
  product_type?: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  signal?: AbortSignal;
}

export const getProductTypesAPI =
  ({
    category,
    quote_id,
    product_type,
    onSuccess,
    onError,
    signal,
  }: GetProductTypesParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        "/products/variants-count-by-component-type",
        {
          params: {
            category,
            quote_id,
            product_type,
          },
          signal,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      if (error.code === "ERR_CANCELED" || error.name === "AbortError") return;
      console.error("Get Product Types Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface DuplicateQuotationParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const duplicateQuotationAPI =
  ({ payload, onSuccess, onError }: DuplicateQuotationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(`/quotes/duplicate`, payload);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Duplicate Quotation error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface CreateRevisionQuotationParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const createRevisionQuotationAPI =
  ({ payload, onSuccess, onError }: CreateRevisionQuotationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/create-revision`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Create Quotation Revision error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface GetQuotationAuditLogListParams {
  filters: any;
  quote_id?: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getQuotationAuditLogListAPI =
  ({ quote_id, filters, onSuccess, onError }: GetQuotationAuditLogListParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/audit-logs/quote-order/${filters?.source_id}`,
        {
          params: filters,
        },
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Dimensions Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetAdditionalInfoFieldsParams {
  category: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getAdditionalInfoFieldsAPI =
  ({ category, onSuccess, onError }: GetAdditionalInfoFieldsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/quotes/additional-info", {
        params: { category },
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Additional Info Fields Error:", error);
      message.error(
        error?.response?.data?.message || "Failed to fetch info options",
      );
      onError?.();
    }
  };

interface ToggleInstallationSwitchAPIParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const toggleInstallationSwitchAPI =
  ({ payload, onSuccess, onError }: ToggleInstallationSwitchAPIParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/quotes/installation-markup-toggle`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Toggle switch error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface UpdateMarginDiscountAPIParams {
  payload: {
    quote_id: string;
    margin?: number | null;
    discount?: number | null;
  };
  onSuccess?: (data: any) => void;
  onError?: (err?: any) => void;
}

export const updateMarginDiscountAPI =
  ({ payload, onSuccess, onError }: UpdateMarginDiscountAPIParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/quotes/margin-discount`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.(result);
      }
    } catch (error: any) {
      console.error("Update margin and discount error:", error);
      onError?.(error);

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage || "Failed to update margin and discount");
    }
  };

interface GetDeliveryCountriesParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getDeliveryCountriesAPI =
  ({ onSuccess, onError }: GetDeliveryCountriesParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get("/country");

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Delivery Countries Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetStatePrefixParams {
  country_id: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getStatePrefixAPI =
  ({ country_id, onSuccess, onError }: GetStatePrefixParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/delivery-postcode-prefix`, {
        params: {
          country_id,
        },
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get State Prefix Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetDeliveryOptionsParams {
  quote_id: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getDeliveryOptionsAPI =
  ({ quote_id, onSuccess, onError }: GetDeliveryOptionsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/delivery-zone-price/quote/${quote_id}/delivery-options`,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Delivery Options Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetUniqueBeamLengthsParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getUniqueBeamLengthsAPI =
  ({ onSuccess, onError }: GetUniqueBeamLengthsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/quotes/products/beam-lengths`);

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Unique Beam Lengths Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetVariantsForLoadNoticeParams {
  quote_id: string;
  component_type: ProductComponentTypeEnum[];
  custom_load_notice?: boolean;
  length?: number | undefined;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getVariantsForLoadNoticeAPI =
  ({
    quote_id,
    component_type,
    custom_load_notice,
    length,
    onSuccess,
    onError,
  }: GetVariantsForLoadNoticeParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/load-notice/quote-variants`, {
        params: {
          quote_id,
          component_type,
          custom_load_notice,
          length,
        },
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Variants For Load Notice Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetBeamValueParams {
  variant_id?: string;
  product_id?: string;
  custom_load_notice?: boolean;
  thickness?: number;
  length?: number | string;
  component_type?: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
  onNoOptionsError?: (messageData: any) => void;
}

export const getBeamValuesForLoadNoticeAPI =
  ({
    variant_id,
    product_id,
    custom_load_notice,
    component_type,
    thickness,
    length,
    onSuccess,
    onError,
    onNoOptionsError,
  }: GetBeamValueParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/load-notice/beam-values`, {
        params: {
          variant_id,
          product_id,
          custom_load_notice,
          component_type,
          thickness,
          length,
        },
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      onError?.();
      // debugger
      const status = error?.response?.status;
      const errorMessage = error?.response?.data?.message;
      if (status === 428) {
        if (onNoOptionsError) {
          onNoOptionsError(error?.response?.data);
        }
      } else {
        message.error(errorMessage || "Something went wrong");
      }
    }
  };

interface GetLoadsForLoadNoticeParams {
  payload: any;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getLoadsForLoadNoticeAPI =
  ({ payload, onSuccess, onError }: GetLoadsForLoadNoticeParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/load-notice/calculate-loads`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Loads For Load Notice Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface UpdateLoadNoticeParams {
  payload: any; // Payload now includes 'id' inside it
  onSuccess: (data: any) => void;
  onError: () => void;
  onWarnings?: (warnings: any[], severity?: WarningSeverity) => void;
}

export const updateLoadNoticeAPI =
  ({ payload, onSuccess, onError, onWarnings }: UpdateLoadNoticeParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/quotes/update/load-notice`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (response.status === 207) {
        console.log("Warnings", result.data?.warnings);
        onWarnings?.(result.data?.warnings || [], result.severity);
        return;
      }

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Load Notice error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface DeleteLoadNoticeParams {
  quote_id: string;
  load_notice_id: string;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const deleteLoadNoticeAPI =
  ({ quote_id, load_notice_id, onSuccess, onError }: DeleteLoadNoticeParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.delete(`/quotes/load-notice`, {
        params: {
          quote_id,
          load_notice_id,
        },
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Delete Load Notice error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface GenerateLoadNoticePdfParams {
  payload: {
    quote_id: string;
    load_notice_id: string;
  };
  onSuccess?: (data: any) => void;
  onError?: () => void;
  onMissingDetailsError?: (messageData: any) => void;
}

export const generateLoadNoticePdfAPI =
  ({
    payload,
    onSuccess,
    onError,
    onMissingDetailsError,
  }: GenerateLoadNoticePdfParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/load-notice/generate-pdf`,
        payload,
        {
          responseType: "blob",
        },
      );

      if (response.data) {
        onSuccess?.(response.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Loads For Load Notice Error:", error);
      onError?.();

      const status = error?.response?.status;

      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorObj = JSON.parse(reader.result as string);
            if (status === 428) {
              if (onMissingDetailsError) {
                onMissingDetailsError(errorObj);
              }
            } else {
              message.error(errorObj.message || "Failed to generate PDF");
            }
          } catch (e) {
            message.error("Failed to generate PDF");
          }
        };
        reader.readAsText(error.response.data);
      } else {
        const errorMessage = error?.response?.data?.message;
        if (status === 428) {
          if (onMissingDetailsError) {
            onMissingDetailsError(error?.response?.data);
          }
        } else {
          message.error(errorMessage || "Failed to generate PDF");
        }
      }
    }
  };

export const generateBOMPdfAPI =
  ({ payload, onSuccess, onError }: any) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/generate-bom-pdf`,
        payload,
        {
          responseType: "blob",
        },
      );

      let fileName = "BOM_Department_Wise.pdf";

      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      if (Capacitor.isNativePlatform()) {
        const reader = new FileReader();

        const blobToBase64 = (blob: Blob) =>
          new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

        try {
          const result: any = await blobToBase64(blob);
          const base64Data = result?.split(",")[1];

          const uniqueFileName = `${Date.now()}_${fileName}`;

          const savedFile = await Filesystem.writeFile({
            path: uniqueFileName,
            data: base64Data,
            directory: Directory.Cache,
          });

          await Share.share({
            url: savedFile.uri,
          });

          onSuccess?.();
        } catch (err) {
          console.error("Native Share Error:", err);
          onError?.();
        }
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        onSuccess?.();
        message.success("BOM PDF downloaded successfully!");
      }
    } catch (error: any) {
      console.error("BOM PDF Generation error:", error);
      onError?.();

      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorObj = JSON.parse(reader.result as string);
            message.error(errorObj.message || "Failed to generate BOM PDF");
          } catch (e) {
            message.error("Failed to generate BOM PDF");
          }
        };
        reader.readAsText(error.response.data);
      } else {
        message.error("Failed to generate BOM PDF");
      }
    }
  };

export const generate2BSOrderBomPdfAPI =
  ({
    order_id,
    onSuccess,
    onError,
  }: {
    order_id: string;
    onSuccess?: () => void;
    onError?: () => void;
  }) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/2bs-order/${order_id}/generate-bom-pdf`,
        { order_id },
        {
          responseType: "blob",
        },
      );

      let fileName = `2BS_Order_BOM_${order_id}.pdf`;

      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      if (Capacitor.isNativePlatform()) {
        const reader = new FileReader();

        const blobToBase64 = (blob: Blob) =>
          new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

        try {
          const result: any = await blobToBase64(blob);
          const base64Data = result?.split(",")[1];

          const uniqueFileName = `${Date.now()}_${fileName}`;

          const savedFile = await Filesystem.writeFile({
            path: uniqueFileName,
            data: base64Data,
            directory: Directory.Cache,
          });

          await Share.share({
            url: savedFile.uri,
          });

          onSuccess?.();
        } catch (err) {
          console.error("Native Share Error:", err);
          onError?.();
        }
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        onSuccess?.();
        message.success("2BS BOM PDF downloaded successfully!");
      }
    } catch (error: any) {
      console.error("2BS BOM PDF Generation error:", error);
      onError?.();

      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorObj = JSON.parse(reader.result as string);
            message.error(errorObj.message || "Failed to generate BOM PDF");
          } catch (e) {
            message.error("Failed to generate BOM PDF");
          }
        };
        reader.readAsText(error.response.data);
      } else {
        message.error(error?.message || "Failed to generate BOM PDF");
      }
    }
  };


interface UpdateDeliveryAddressParams {
  payload: any; // Payload now includes 'id' inside it
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const updateDeliveryAddressForQuoteOrderAPI =
  ({ payload, onSuccess, onError }: UpdateDeliveryAddressParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.put(
        `/quotes/orders/delivery-address`,
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success(result?.message);
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Update Delivery Address error:", error);
      onError?.();

      const errorMessage = error?.response?.data?.message;
      message.error(errorMessage);
    }
  };

interface GetClientContactsParams {
  clientId: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getClientContactsAPI =
  ({ clientId, onSuccess, onError }: GetClientContactsParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/customers/by-id`, {
        params: {
          id: clientId,
        },
      });

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Client Contacts Error:", error);
      message.error(error?.response?.data?.message);
      onError?.();
    }
  };

interface GetLatestExchangeRateParams {
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const getLatestExchangeRateAPI =
  ({ onSuccess, onError }: GetLatestExchangeRateParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(`/quotes/exchange-rate/latest`);
      let result = responseDataFormate(response.data);
      console.log("getLatestExchangeRateAPI result:", result);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message);
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Exchange Rate Error:", error);
      message.error(
        error?.response?.data?.message || "Failed to fetch exchange rate",
      );
      onError?.();
    }
  };

interface GenerateBomParams {
  payload: any;
  onSuccess: (data: any) => void;
  onError: () => void;
}

export const generateBomAPI =
  ({ payload, onSuccess, onError }: GenerateBomParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        "/quotes/generate-bom",
        payload,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        onSuccess?.(result.data);
      } else {
        message.error(result?.message || "Failed to generate BOM");
        onError?.();
      }
    } catch (error: any) {
      console.error("Generate BOM error:", error);
      onError?.();
      const errorMessage =
        error?.response?.data?.message || "An error occurred";
      message.error(errorMessage);
    }
  };

export const downloadBomPdfAPI =
  ({ payload, onSuccess, onError }: any) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.post(
        `/quotes/download-bom-pdf`,
        payload,
        {
          responseType: "blob",
        },
      );

      let fileName = "BOM_Search.pdf";

      const contentDisposition = response.headers["content-disposition"];

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) {
          fileName = fileNameMatch[1];
        }
      }

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      if (Capacitor.isNativePlatform()) {
        const reader = new FileReader();

        const blobToBase64 = (blob: Blob) =>
          new Promise((resolve, reject) => {
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });

        try {
          const result: any = await blobToBase64(blob);
          const base64Data = result?.split(",")[1];

          const uniqueFileName = `${Date.now()}_${fileName}`;

          const savedFile = await Filesystem.writeFile({
            path: uniqueFileName,
            data: base64Data,
            directory: Directory.Cache,
          });

          await Share.share({
            url: savedFile.uri,
          });

          onSuccess?.();
        } catch (err) {
          console.error("Native Share Error:", err);
          onError?.();
        }
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();

        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);

        onSuccess?.();
        message.success("BOM PDF downloaded successfully!");
      }
    } catch (error: any) {
      console.error("PDF Generation error:", error);
      onError?.();

      if (error.response && error.response.data instanceof Blob) {
        const reader = new FileReader();
        reader.onload = () => {
          try {
            const errorObj = JSON.parse(reader.result as string);
            message.error(errorObj.message || "Failed to download BOM PDF");
          } catch (e) {
            message.error("Failed to download BOM PDF");
          }
        };
        reader.readAsText(error.response.data);
      } else {
        message.error("Failed to download BOM PDF");
      }
    }
  };

interface RequestCustomerApprovalParams {
  customerId: string;
  onSuccess?: (data: any) => void;
  onError?: () => void;
}

export const requestCustomerApprovalAPI =
  ({ customerId, onSuccess, onError }: RequestCustomerApprovalParams) =>
  async (dispatch: any) => {
    const key = "request-approval-toast";
    message.loading({
      content: "Sending approval request to admin...",
      key,
      duration: 0,
    });
    try {
      const response = await axiosInstance.post(
        `/customers/${customerId}/request-approval`,
      );

      let result = responseDataFormate(response.data);

      if (result?.success) {
        message.success({
          content:
            result?.message ||
            "Customer approval request sent to Admin successfully",
          key,
          duration: 3,
        });
        onSuccess?.(result.data);
      } else {
        message.error({
          content:
            result?.message || "Failed to send customer approval request",
          key,
          duration: 3,
        });
        onError?.();
      }
    } catch (error: any) {
      console.error("Request Customer Approval Error:", error);
      message.error({
        content:
          error?.response?.data?.message ||
          "Unable to send customer approval request",
        key,
        duration: 3,
      });
      onError?.();
    }
  };

export interface CheckDuplicateCustomerParams {
  email?: string;
  company?: string;
  excludeId?: string;
  onSuccess?: (data: any) => void;
  onDuplicateFound?: (data: any) => void;
  onError?: () => void;
}

export const checkDuplicateCustomerAPI =
  ({
    email,
    company,
    excludeId,
    onSuccess,
    onDuplicateFound,
    onError,
  }: CheckDuplicateCustomerParams) =>
  async (dispatch: any) => {
    try {
      const params: Record<string, string> = {};
      if (email) params.email = email;
      if (company) params.company = company;
      if (excludeId) params.exclude_id = excludeId;

      const response = await axiosInstance.get(`/customers/duplicate-check`, {
        params,
      });

      const result = responseDataFormate(response.data);
      if (result?.success) {
        onSuccess?.(result?.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 428) {
        if (onDuplicateFound) {
          onDuplicateFound(error?.response?.data);
        }
      } else {
        onError?.();
      }
    }
  };

export interface ReverseCalculationData {
  quote_id: string;
  display_quote_id?: string;
  currency: string;
  initial_total: number;
  remaining_balance: number;
  is_balanced: boolean;
  total_deductions_count: number;
}

interface GetQuoteReverseCalculationParams {
  quote_id: string;
  onSuccess?: (data: ReverseCalculationData) => void;
  onError?: () => void;
}

export const getQuoteReverseCalculationAPI =
  ({ quote_id, onSuccess, onError }: GetQuoteReverseCalculationParams) =>
  async (dispatch: any) => {
    try {
      const response = await axiosInstance.get(
        `/quotes/${quote_id}/reverse-calculation`,
      );
      const result = responseDataFormate(response.data);
      if (result?.success) {
        onSuccess?.(result?.data);
      } else {
        onError?.();
      }
    } catch (error: any) {
      console.error("Get Quote Reverse Calculation error:", error);
      onError?.();
    }
  };

